import type { Editor } from "@tiptap/react";

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { EditorContent } from "@tiptap/react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, AtSign, MousePointerClick, Square, X } from "lucide-react";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

import { FilePreview } from "./FilePreview";
import { useAutosizeTextArea } from "./use-autosize-textarea";

import "./MessageInput/styles.css";

import type { DisplayMode } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";

import { useCurrentChatContext } from "@editor/contexts/CurrentChatContext";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import { useNextDevServer } from "@/features/code-execution/context";
import { normalizeRoute } from "@/features/code-execution/utils";
import {
  useActiveTriggerJobChats,
  useActiveTriggerJobsForChat,
  useStreamingChats,
} from "@/features/react-editor/components/chat/store/ChatStoreContext";
import ElementTypeTag from "@/features/react-editor/components/modifiers/ElementTypeTag";
import {
  useDefaultSelectedElementDesignation,
  useModifiersStoreActions,
} from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { useCurrentFile } from "@/features/react-editor/utils/useCurrentFile";
import Popover from "@replo/design-system/components/popover/Popover";
import twMerge from "@replo/design-system/utils/twMerge";
import { useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { ContextItemsMenu } from "./ContextItemsMenu/ContextItemsMenu";
import useChatMessageLimits from "./hooks/useChatMessageLimits";
import { ActionsPopover } from "./MessageInput/ActionsPopover";
import { InspirationMenu } from "./MessageInput/InspirationMenu";

interface MessageInputBaseProps {
  className?: string;
  disabled?: boolean;
  stop?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  enableInterrupt?: boolean;
  editor: Editor | null;
  isChatLoading: boolean;
  displayMode?: DisplayMode;
  toggleDisplayMode?: () => void;
  editorClassName?: string;
  variant?: "default" | "gradient";
  isMessageLimitShowing?: boolean;
}

interface MessageInputProps extends MessageInputBaseProps {
  allowAttachments: true;
  files: File[] | null;
  setFiles: React.Dispatch<React.SetStateAction<File[] | null>>;
}

export interface MessageInputRef {
  triggerFileUpload: () => Promise<void>;
  triggerContextMenu: () => void;
}

export const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(
  function MessageInput(
    {
      className,
      stop,
      enableInterrupt = true,
      disabled,
      editor,
      isChatLoading,
      displayMode,
      toggleDisplayMode,
      editorClassName,
      variant = "default",
      isMessageLimitShowing = false,
      ...props
    },
    ref,
  ) {
    const logEvent = useLogAnalytics();
    const file = useCurrentFile();
    const pageRoute = file?.pageRoute ?? null;
    const { pageStatusMap } = useNextDevServer();
    const isPageReady = useMemo(() => {
      if (!pageRoute) {
        return false;
      }
      const statusRecord = pageStatusMap[normalizeRoute(pageRoute)];
      if (!statusRecord) {
        return false;
      }
      return statusRecord.status === 200;
    }, [pageRoute, pageStatusMap]);
    const [searchParams] = useSearchParams();
    const pageId = searchParams.get("pageId");
    const chatId = searchParams.get("chatId");
    const [isDragging, setIsDragging] = useState(false);
    const [showInterruptPrompt, setShowInterruptPrompt] = useState(false);
    const streamingChats = useStreamingChats();
    const activeTriggerJobChats = useActiveTriggerJobChats();
    const activeTriggerJobsForChat = useActiveTriggerJobsForChat(chatId);
    const { messagesRemaining, hasUnlimitedMessages } = useChatMessageLimits();
    const hasUserReachedMessageLimit =
      !hasUnlimitedMessages && messagesRemaining <= 0;
    const { isContextMenuOpen, dispatchCurrentChat, messagePopoverType } =
      useCurrentChatContext();

    const activeChats = Array.from(
      new Set([...streamingChats, ...activeTriggerJobChats]),
    );

    const isLoading = isChatLoading || activeTriggerJobsForChat.length > 0;

    useOnValueChange(isLoading, (currentValue, previousValue) => {
      if (previousValue && !currentValue) {
        setShowInterruptPrompt(false);
      }
    });

    const addFiles = useCallback(
      (files: File[] | null) => {
        if (props.allowAttachments && files) {
          props.setFiles((currentFiles) => {
            if (currentFiles === null) {
              return files;
            }
            return [...currentFiles, ...files];
          });
        }
      },
      [props.allowAttachments, props.setFiles],
    );

    useImperativeHandle(
      ref,
      () => ({
        triggerFileUpload: async () => {
          if (props.allowAttachments) {
            const files = await showFileUploadDialog();
            addFiles(files);
          }
        },
        triggerContextMenu: () => {
          // Use setTimeout to delay opening the context menu until after the editor update has processed
          setTimeout(() => {
            dispatchCurrentChat({ type: "openContextMenu" });
            if (editor) {
              editor.commands.focus();
            }
          }, 10);
        },
      }),
      [props.allowAttachments, dispatchCurrentChat, addFiles, editor],
    );

    const onDragOver = (event: React.DragEvent) => {
      if (props.allowAttachments !== true) {
        return;
      }
      event.preventDefault();
      setIsDragging(true);
    };

    const onDragLeave = (event: React.DragEvent) => {
      if (props.allowAttachments !== true) {
        return;
      }
      event.preventDefault();
      setIsDragging(false);
    };

    const onDrop = (event: React.DragEvent) => {
      setIsDragging(false);
      if (props.allowAttachments !== true) {
        return;
      }
      event.preventDefault();
      const dataTransfer = event.dataTransfer;
      if (dataTransfer.files.length) {
        addFiles(Array.from(dataTransfer.files));
      }
    };

    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const onContainerMouseDown = (e: React.MouseEvent) => {
      if (!editor) {
        return;
      }

      const target = e.target as HTMLElement;
      const isClickableElement = target.closest(
        'button, input, textarea, [role="button"], [tabindex]',
      );

      if (!isClickableElement) {
        e.preventDefault();
        editor.chain().focus().run();
      }
    };

    React.useEffect(() => {
      if (!editor) {
        return;
      }
      const id = requestAnimationFrame(() => editor.chain().focus().run());
      return () => cancelAnimationFrame(id);
    }, [editor]);

    const handleAtSignClick = () => {
      if (isContextMenuOpen) {
        dispatchCurrentChat({ type: "closeContextMenu" });
      } else {
        dispatchCurrentChat({ type: "openContextMenu" });
      }
    };

    const showFileList =
      props.allowAttachments && props.files && props.files.length > 0;

    useAutosizeTextArea({
      ref: textAreaRef,
      maxHeight: 240,
      borderWidth: 1,
      dependencies: [showFileList],
    });

    const containerClassName = twMerge(
      "relative w-full transition-[border] bg-white rounded-lg p-3 pr-3",
      variant === "gradient" && "rounded-3xl bg-none",
      !isMessageLimitShowing && "rounded-xl bg-white border border-border",
    );

    const containerStyle =
      variant === "gradient"
        ? {
            backgroundColor: "#FFFFFF",
            border: "8px solid rgba(255, 255, 255, 0.6)",
            backgroundClip: "padding-box",
          }
        : undefined;

    const isActiveChatsLimitReached = Boolean(
      activeChats.length > 5 && chatId && !activeChats.includes(chatId),
    );

    const getSubmitMessageTooltipText = () => {
      if (hasUserReachedMessageLimit) {
        return "You've used all your messages";
      }
      if (isActiveChatsLimitReached) {
        return "You can only have 5 requests running at a time";
      }
      return undefined;
    };

    const handleOpenChange = (isOpen: boolean) => {
      if (!isOpen) {
        if (messagePopoverType === "contextMenu") {
          dispatchCurrentChat({ type: "closeContextMenu" });
        }
        if (messagePopoverType === "inspiration") {
          dispatchCurrentChat({ type: "closeInspirationMenu" });
        }
      }
    };

    const popoverContent = messagePopoverType
      ? exhaustiveSwitch({ type: messagePopoverType })({
          inspiration: (
            <InspirationMenu
              editor={editor}
              onClose={() =>
                dispatchCurrentChat({ type: "closeInspirationMenu" })
              }
            />
          ),
          contextMenu: (
            <ContextItemsMenu
              editor={editor}
              onClose={() => dispatchCurrentChat({ type: "closeContextMenu" })}
            />
          ),
        })
      : null;

    return (
      <Popover.Root
        isOpen={Boolean(messagePopoverType)}
        onOpenChange={handleOpenChange}
        shouldIgnoreOutsideInteractions
      >
        <Popover.Anchor className="w-full">
          <div
            ref={containerRef}
            className={containerClassName}
            style={containerStyle}
            onMouseDown={onContainerMouseDown}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            {enableInterrupt && (
              <InterruptPrompt
                isOpen={showInterruptPrompt}
                close={() => setShowInterruptPrompt(false)}
              />
            )}
            {pageId && <SelectedElementIndicator />}
            {props.allowAttachments &&
              props.files &&
              props.files.length > 0 && (
                <div className="z-20 overflow-x-auto py-2 pb-3">
                  <div className="flex space-x-3">
                    <AnimatePresence mode="popLayout">
                      {props.files?.map((file) => {
                        return (
                          <FilePreview
                            key={file.name + String(file.lastModified)}
                            file={file}
                            onRemove={() => {
                              props.setFiles((files) => {
                                if (!files) {
                                  return null;
                                }

                                const filtered = Array.from(files).filter(
                                  (f) => f !== file,
                                );
                                if (filtered.length === 0) {
                                  return null;
                                }
                                return filtered;
                              });
                            }}
                          />
                        );
                      })}
                    </AnimatePresence>
                  </div>
                </div>
              )}

            {props.allowAttachments && (
              <FileUploadOverlay isDragging={isDragging} />
            )}
            <EditorContent
              className={twMerge(
                "w-full pb-3 overflow-y-auto",
                editorClassName,
              )}
              editor={editor}
            />

            <div className="z-20 flex flex-row justify-between gap-2 w-full pt-2">
              <div className="flex gap-2">
                <IconButton
                  variant="secondary"
                  icon={<AtSign className="h-4 w-4 text-muted" />}
                  onClick={handleAtSignClick}
                  id="at-sign-button"
                />
                <div>
                  <ActionsPopover
                    handleAttachImages={async () => {
                      const files = await showFileUploadDialog();
                      addFiles(files);
                    }}
                  />
                </div>

                {/*
                NOTE (Kurt, 2025-08-06): The <PageRenderer> component only shows up in the editor when
                there is a pageId in the URL. This ensures that the select button only shows up in the
                message input when there is a page available.
                */}
                {pageId && toggleDisplayMode && Boolean(displayMode) && (
                  <Button
                    size="base"
                    variant="secondary"
                    disabled={!isPageReady}
                    UNSAFE_className={twMerge(
                      "max-sm:h-8 max-sm:py-1.5 max-sm:text-base",
                      displayMode === "select" &&
                        "bg-primary/10 border border-primary hover:bg-primary/10 text-primary",
                    )}
                    startEnhancer={
                      <MousePointerClick
                        className={twMerge(
                          "h-4 w-4",
                          displayMode === "select" && "text-primary",
                        )}
                      />
                    }
                    onClick={() => {
                      toggleDisplayMode();
                      if (displayMode !== "select") {
                        logEvent("editor.modifier.enable");
                      }
                    }}
                  >
                    Edit
                  </Button>
                )}
              </div>

              {isLoading && stop ? (
                <IconButton
                  variant="secondary"
                  type="button"
                  size="base"
                  aria-label="Stop generating"
                  onClick={stop}
                  icon={
                    <Square
                      className="h-3 w-3 animate-pulse"
                      fill="currentColor"
                    />
                  }
                />
              ) : (
                <IconButton
                  type="submit"
                  size="base"
                  variant="primary"
                  aria-label="Send message"
                  disabled={
                    hasUserReachedMessageLimit || isActiveChatsLimitReached
                  }
                  tooltipText={getSubmitMessageTooltipText()}
                  icon={<ArrowUp className="h-4 w-4" />}
                />
              )}
            </div>
          </div>
        </Popover.Anchor>
        <Popover.Content
          side="top"
          shouldPreventDefaultOnInteractOutside={false}
          className="p-0 border-0.5 border-border overflow-hidden"
          style={{
            width: "var(--radix-popper-anchor-width)",
          }}
          collisionPadding={60}
          hideCloseButton
        >
          {popoverContent}
        </Popover.Content>
      </Popover.Root>
    );
  },
);

interface InterruptPromptProps {
  isOpen: boolean;
  close: () => void;
}

function InterruptPrompt({ isOpen, close }: InterruptPromptProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ top: 0, filter: "blur(5px)" }}
          animate={{
            top: -40,
            filter: "blur(0px)",
            transition: {
              type: "spring",
              filter: { type: "tween" },
            },
          }}
          exit={{ top: 0, filter: "blur(5px)" }}
          className="absolute left-1/2 flex -translate-x-1/2 overflow-hidden whitespace-nowrap rounded-full border bg-background py-1 text-center text-sm text-muted-foreground"
        >
          <span className="ml-2.5">Press Enter again to interrupt</span>
          <button
            className="ml-1 mr-2.5 flex items-center"
            type="button"
            onClick={close}
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface FileUploadOverlayProps {
  isDragging: boolean;
}

function FileUploadOverlay({ isDragging }: FileUploadOverlayProps) {
  return (
    <AnimatePresence>
      {isDragging && (
        <motion.div
          className="pointer-events-none flex items-center gap-3 p-3 rounded-lg border border-dashed border-border bg-white text-sm text-muted mb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          aria-hidden
        >
          <img
            src="/images/chat/files-graphic.svg"
            alt="File upload graphic"
            width={52}
            height={52}
          />
          <div>
            <div className="font-medium mb-1">
              Drop your images here to attach them.
            </div>
            <div>PNG, JPEG, GIF, or WEBP only</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function showFileUploadDialog() {
  const input = document.createElement("input");

  input.type = "file";
  input.multiple = true;
  input.accept =
    ".png,.jpeg,.jpg,.gif,.webp,image/png,image/jpeg,image/gif,image/webp";
  input.click();

  return new Promise<File[] | null>((resolve) => {
    input.addEventListener("change", (e) => {
      const files = (e.currentTarget as HTMLInputElement).files;

      if (files) {
        const fileArray = Array.from(files);
        resolve(fileArray.length > 0 ? fileArray : null);
        return;
      }

      resolve(null);
    });
  });
}

/*
 * NOTE (Kevin, 2025-08-11): Selected element indicator shown above the editor input when a selection exists.
 * Displays element type via ElementTypeTag and a short preview of the element's childText.
 * Includes a clear button to unselect the element.
 */
function SelectedElementIndicator() {
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const modifierActions = useModifiersStoreActions();

  if (!defaultDesignation) {
    return null;
  }

  return (
    <div className="mb-2 -mt-1 inline-flex items-center gap-1">
      <div className="cursor-default">
        <ElementTypeTag
          elementType={defaultDesignation.element.type}
          showRemoveOnHover
          onRemove={() => modifierActions.setSelectedElementDesignations(null)}
        />
      </div>
    </div>
  );
}
